import { myObjectKeys } from '@/global/function';
import { nonNullableStr } from '@/lib/util';
import {
  validateAdditionalDistanceFee,
  validateBaseDeliveryFee,
  validateFixedDiscountAmount,
  validateUberDirectCustomerId,
} from '@/pages/camel-order/delivery/validate';
import { SiteDeliveryResponse } from '@/services/Delivery/hooks/list/useCamelOrderDeliveryList';
import { DeliveryUpsertParams } from '@/services/Delivery/hooks/upsert/useCamelOrderDeliveryUpsert';

import {
  DeliveryFormError,
  validateAdditionalDistance,
  validateBaseDeliveryFeeDistance,
} from './validate';

const baseDeliveryFee = {
  key: 'base_delivery_fee',
  label: '基本配送料',
} as const;
const baseDeliveryFeeDistance = {
  key: 'base_delivery_fee_distance',
  label: '基本配送料の距離',
} as const;
const additionalDistanceFee = {
  key: 'additional_distance_fee',
  label: '追加距離料金',
} as const;
const additionalDistance = {
  key: 'additionalDistance',
  label: '追加距離料金の距離',
} as const;
const fixedDiscountAmount = {
  key: 'fixed_discount_amount',
  label: '固定割引額',
} as const;
const uberDirectCustomerId = {
  key: 'uberDirectCustomerId',
  label: 'customer id（利用者 ID）',
} as const;
export const camelOrderDeliveryForm = {
  baseDeliveryFee,
  baseDeliveryFeeDistance,
  additionalDistanceFee,
  additionalDistance,
  fixedDiscountAmount,
  uberDirectCustomerId,
} as const;

export const deliveryFeeTypeMap = {
  DELIVERY_PROVIDER_FEE: '配送事業者の提示する配送料',
  DELIVERY_PROVIDER_FEE_WITH_DISCOUNT:
    '配送事業者の提示する配送料から固定で割引をする',
  BASE_DELIVERY_FEE: '固定配送料のみ',
  BASE_DELIVERY_FEE_AND_ADDITIONAL_DISTANCE_FEE: '基本配送料＋追加距離料金',
} as const;

export const DELIVERY_FEE_TYPES = myObjectKeys(deliveryFeeTypeMap).map(
  (type) => {
    return {
      value: type,
      label: deliveryFeeTypeMap[type],
    };
  }
);

export type DeliveryFeeType = keyof typeof deliveryFeeTypeMap;

export const preferenceTypeMap = {
  DELIVERY_FEE: '最も配送料が安い事業者を選択',
  DROPOFF_ETA: '最も配達が速い事業者を選択',
} as const;

export const PREFERENCE_TYPES = myObjectKeys(preferenceTypeMap).map((type) => {
  return {
    value: type,
    label: preferenceTypeMap[type],
  };
});

export type PreferenceType = keyof typeof preferenceTypeMap;

export type DeliveryProvider = 'UBER_DIRECT' | 'WOLT_DRIVE';

const MIN_DELIVERY_FEES: { [key in DeliveryProvider]: number } = {
  UBER_DIRECT: 690,
  WOLT_DRIVE: 690, // 暫定の値
};

export const getMinDeliveryFee = (
  deliveryProviders: DeliveryProvider[]
): number => {
  const fees = deliveryProviders.map(
    (deliveryProvider) => MIN_DELIVERY_FEES[deliveryProvider]
  );
  return Math.min(...fees);
};

type Delivery = {
  id: string;
  delivery_fee_type: DeliveryFeeType;
  delivery_providers: DeliveryProvider[];
  base_delivery_fee: string;
  base_delivery_fee_distance: string;
  additional_distance_fee: string;
  additional_distance: string;
  fixed_discount_amount: string;
  preference_type: PreferenceType;
  uber_direct_customer_id: string;
  wolt_drive_merchant_id: string;
  wolt_drive_key: string;
};

export type SiteDelivery = {
  site_id: string;
  site_url: string;
  delivery: Delivery;
};

export const initializeSites = (
  resSites: SiteDeliveryResponse[]
): SiteDelivery[] => {
  const buildSites: SiteDelivery[] = resSites.map((site) => {
    const { site_id, site_url, delivery } = site;
    const base_delivery_fee = delivery?.base_delivery_fee ?? '';
    const base_delivery_fee_distance =
      delivery?.base_delivery_fee_distance ?? '';
    const additional_distance_fee = delivery?.additional_distance_fee ?? '';
    const additional_distance = delivery?.additional_distance ?? '';
    const fixed_discount_amount = delivery?.fixed_discount_amount ?? '';
    const uber_direct_customer_id = delivery?.uber_direct_customer_id ?? '';
    const wolt_drive_merchant_id = delivery?.wolt_drive_merchant_id ?? '';
    const wolt_drive_key = delivery?.wolt_drive_key ?? '';
    const preference_type = delivery?.preference_type ?? 'DELIVERY_FEE';
    return {
      site_id,
      site_url,
      delivery: {
        id: delivery?.id ?? '',
        delivery_fee_type:
          delivery?.delivery_fee_type ?? 'DELIVERY_PROVIDER_FEE',
        delivery_providers: delivery?.delivery_providers ?? ['UBER_DIRECT'], // 一つしかないので入れておく
        base_delivery_fee: nonNullableStr(base_delivery_fee),
        base_delivery_fee_distance: nonNullableStr(base_delivery_fee_distance),
        additional_distance_fee: nonNullableStr(additional_distance_fee),
        additional_distance: nonNullableStr(additional_distance),
        fixed_discount_amount: nonNullableStr(fixed_discount_amount),
        preference_type,
        uber_direct_customer_id,
        wolt_drive_merchant_id,
        wolt_drive_key,
      },
    };
  });
  return buildSites;
};

export const checkError = (
  targetSite: SiteDelivery,
  error: DeliveryFormError
): DeliveryFormError => {
  const {
    base_delivery_fee,
    base_delivery_fee_distance,
    additional_distance_fee,
    additional_distance,
    fixed_discount_amount,
    delivery_fee_type,
    uber_direct_customer_id,
  } = targetSite.delivery;
  if (targetSite.delivery.delivery_providers.includes('UBER_DIRECT')) {
    error = validateUberDirectCustomerId(uber_direct_customer_id, error);
  }
  if (delivery_fee_type === 'BASE_DELIVERY_FEE') {
    error = validateBaseDeliveryFee(base_delivery_fee, error);
  } else if (delivery_fee_type === 'DELIVERY_PROVIDER_FEE_WITH_DISCOUNT') {
    error = validateFixedDiscountAmount(
      fixed_discount_amount,
      getMinDeliveryFee(targetSite.delivery.delivery_providers),
      error
    );
  } else if (
    delivery_fee_type === 'BASE_DELIVERY_FEE_AND_ADDITIONAL_DISTANCE_FEE'
  ) {
    error = validateBaseDeliveryFee(base_delivery_fee, error);
    error = validateBaseDeliveryFeeDistance(base_delivery_fee_distance, error);
    error = validateAdditionalDistanceFee(additional_distance_fee, error);
    error = validateAdditionalDistance(additional_distance, error);
  }
  return error;
};

export const buildUpsertParams = (
  targetSite: SiteDelivery
): DeliveryUpsertParams => {
  const { delivery } = targetSite;
  const {
    delivery_fee_type,
    delivery_providers,
    base_delivery_fee,
    base_delivery_fee_distance,
    additional_distance_fee,
    additional_distance,
    fixed_discount_amount,
    preference_type,
    uber_direct_customer_id,
    wolt_drive_merchant_id,
    wolt_drive_key,
  } = delivery;

  const baseDeliveryFee = (() => {
    if (delivery_fee_type !== 'BASE_DELIVERY_FEE') return null;
    return base_delivery_fee !== '' ? Number(base_delivery_fee) : null;
  })();

  const fixedDiscountAmount = (() => {
    if (delivery_fee_type !== 'DELIVERY_PROVIDER_FEE_WITH_DISCOUNT')
      return null;
    return fixed_discount_amount !== '' ? Number(fixed_discount_amount) : null;
  })();

  const baseDeliverySettingValue = (value: string) => {
    if (delivery_fee_type !== 'BASE_DELIVERY_FEE_AND_ADDITIONAL_DISTANCE_FEE')
      return null;
    return value !== '' ? Number(value) : null;
  };

  return {
    site_id: targetSite.site_id,
    delivery_fee_type: delivery_fee_type,
    base_delivery_fee: baseDeliveryFee,
    base_delivery_fee_distance: baseDeliverySettingValue(
      base_delivery_fee_distance
    ),
    additional_distance_fee: baseDeliverySettingValue(additional_distance_fee),
    additional_distance: baseDeliverySettingValue(additional_distance),
    fixed_discount_amount: fixedDiscountAmount,
    preference_type: preference_type,
    delivery_providers: delivery_providers,
    uber_direct_customer_id:
      uber_direct_customer_id !== '' ? uber_direct_customer_id : null,
    wolt_drive_merchant_id:
      wolt_drive_merchant_id !== '' ? wolt_drive_merchant_id : null,
    wolt_drive_key: wolt_drive_key !== '' ? wolt_drive_key : null,
  };
};
